import { useMemo } from "react";
import { useNavigate } from "react-router";
import Skeleton from "react-loading-skeleton";
import cx from "classnames";

import Box from "ds/components/Box";
import CardContent from "ds/components/Card/components/Content";
import CardHeader from "ds/components/Card/components/Header";
import CardWrapper from "ds/components/Card/components/Wrapper";
import DropdownMenuEllipsis from "ds/components/DropdownMenu/Ellipsis";
import DropdownMenuItem from "ds/components/DropdownMenu/Item";
import TileContent from "ds/components/Tile/Content";
import TileWrapper from "ds/components/Tile/Wrapper";
import useUserGuideCertificates from "shared/UserGuides/useUserGuideCertificates";

import LaunchPadLearnCertificateTile from "./CertificateTile";
import styles from "./styles.module.css";

type CertificatePreviewGroup = {
  id: string;
  name: string;
  timeToComplete: string;
  guidesCount: number;
  guidesCompletedCount: number;
};

type LaunchPadLearnCertificatePreviewProps = {
  userGuideGroups: CertificatePreviewGroup[] | undefined;
};

const SKELETON_TILES = Array.from({ length: 4 }, (_, i) => i);

const LaunchPadLearnCertificatePreview = ({
  userGuideGroups,
}: LaunchPadLearnCertificatePreviewProps) => {
  const navigate = useNavigate();
  const { getCertificateByGroupId, loading: certsLoading } = useUserGuideCertificates();

  const groups = useMemo(
    () =>
      userGuideGroups
        ?.map((g) => ({
          ...g,
          certificate: getCertificateByGroupId(g.id),
          allGuidesCompleted: g.guidesCount > 0 && g.guidesCompletedCount === g.guidesCount,
        }))
        .sort((a, b) => Number(b.allGuidesCompleted) - Number(a.allGuidesCompleted)),
    [userGuideGroups, getCertificateByGroupId]
  );

  // Disable the overlay if the user has 3 or more completed groups, it has to be changed when we add more missions in the future and design align it
  const hasOverlayDisabled = useMemo(() => {
    return (groups?.filter((g) => g.allGuidesCompleted).length ?? 0) >= 3;
  }, [groups]);

  const handleSeeAll = () => navigate("/mission-control");

  if (!groups || certsLoading) {
    return (
      <CardWrapper className={styles.wrapper}>
        <CardHeader titleTag="h3">
          <Skeleton width="12rem" height="1.6rem" />
        </CardHeader>
        <CardContent hasHeader className={styles.content} grid gap="x-large">
          {SKELETON_TILES.map((i) => (
            <TileWrapper key={i}>
              <TileContent gap="large">
                <Skeleton circle width="4rem" height="4rem" />
                <Box direction="column" gap="small" grow="1">
                  <Skeleton width="80%" height="1.2rem" />
                  <Skeleton width="60%" height="1rem" />
                </Box>
              </TileContent>
            </TileWrapper>
          ))}
        </CardContent>
      </CardWrapper>
    );
  }

  return (
    <CardWrapper className={cx(styles.wrapper, hasOverlayDisabled && styles.overlayDisabled)}>
      <CardHeader
        titleTag="h3"
        suffix={
          <DropdownMenuEllipsis tooltip="Certificate options">
            <DropdownMenuItem onAction={handleSeeAll}>See all</DropdownMenuItem>
          </DropdownMenuEllipsis>
        }
      >
        Your certificates
      </CardHeader>

      <CardContent hasHeader className={styles.content} grid gap="x-large">
        {groups.map((group) =>
          group.certificate ? (
            <LaunchPadLearnCertificateTile
              key={group.id}
              certificate={group.certificate}
              groupId={group.id}
              groupName={group.name}
            />
          ) : (
            <LaunchPadLearnCertificateTile
              key={group.id}
              groupId={group.id}
              groupName={group.name}
              timeToComplete={group.timeToComplete}
              allGuidesCompleted={group.allGuidesCompleted}
              guidesCount={group.guidesCount}
            />
          )
        )}
      </CardContent>
    </CardWrapper>
  );
};

export default LaunchPadLearnCertificatePreview;
